import { Notice } from "@/components/ui/notice";
import { pluralize } from "@/lib/format";
import ContactText from "@/components/ContactText";

/**
 * What went wrong with a CSV upload (classrooms or teachers). A
 * successful import redirects with a notice, so this only reports
 * problems: row errors (nothing was saved) and warnings.
 */
export default function ImportReport({ report }) {
  if (!report) return null;
  const warnings = report.warnings || [];

  return (
    <div className="space-y-4">
      {!report.success && report.errors?.length > 0 && (
        <Notice tone="destructive" title="Nothing was saved. Fix these rows and upload the file again.">
          <ul className="mt-1 list-disc space-y-1 pl-5">
            {report.errors.map((error, i) => (
              <li key={i}>
                {error.line ? `Line ${error.line}: ` : ""}
                <ContactText text={error.errors.join(", ")} subject={error.contact_subject} />
              </li>
            ))}
          </ul>
        </Notice>
      )}
      {warnings.length > 0 && (
        <Notice tone="warning" title={pluralize(warnings.length, "thing", "things") + " to check"}>
          <ul className="mt-1 list-disc space-y-1 pl-5">
            {warnings.slice(0, 5).map((warning, i) => <li key={i}>{warning.message}</li>)}
            {warnings.length > 5 && <li>…and {warnings.length - 5} more.</li>}
          </ul>
        </Notice>
      )}
    </div>
  );
}

// The file picker both import pages use.
export function CsvFileInput({ id = "file", label, onChange, describedBy, columns }) {
  return (
    <div>
      <label htmlFor={id} className="mb-2 block text-sm font-medium text-foreground">
        {label} <span className="text-destructive">*</span>
      </label>
      <input
        id={id}
        name="file"
        type="file"
        accept=".csv,text/csv"
        required
        aria-describedby={describedBy}
        onChange={(e) => onChange(e.target.files[0])}
        className="block w-full cursor-pointer rounded-lg border border-border bg-card p-3 text-sm text-foreground file:mr-4 file:cursor-pointer file:rounded-lg file:border-0 file:bg-primary/10 file:px-4 file:py-2.5 file:font-medium file:text-primary hover:file:bg-primary/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      />
      <p className="mt-2 text-xs text-muted-foreground">Up to 5 MB. Columns: {columns}.</p>
    </div>
  );
}
